/** Formatting helpers. All money is stored/transported as a number of dollars. */

const currencyCache = new Map<string, Intl.NumberFormat>();

function currencyFormatter(currency: string, opts?: Intl.NumberFormatOptions) {
  const key = currency + JSON.stringify(opts ?? {});
  let f = currencyCache.get(key);
  if (!f) {
    f = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      ...opts
    });
    currencyCache.set(key, f);
  }
  return f;
}

export function money(amount: number | null | undefined, currency = 'USD') {
  if (amount == null || Number.isNaN(amount)) return '—';
  return currencyFormatter(currency).format(amount);
}

/** Money without cents — for big roll-up numbers on dashboards. */
export function moneyCompact(amount: number | null | undefined, currency = 'USD') {
  if (amount == null || Number.isNaN(amount)) return '—';
  return currencyFormatter(currency, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  }).format(amount);
}

export function hours(value: number | null | undefined) {
  if (value == null || Number.isNaN(value)) return '—';
  // Hours are entered as decimals (1.25). Trim trailing zeros but keep >= 1dp
  // so a column of hours lines up.
  const rounded = Math.round(value * 100) / 100;
  return rounded.toFixed(rounded % 1 === 0 ? 2 : 2);
}

export function hoursShort(value: number | null | undefined) {
  if (value == null || Number.isNaN(value)) return '0';
  const rounded = Math.round(value * 100) / 100;
  return rounded % 1 === 0 ? String(rounded) : rounded.toFixed(2).replace(/0$/, '');
}

/**
 * Parse a user-typed duration. Accepts `1.5`, `1:30`, `90m`, `1h30`, `1h 30m`.
 * Returns null when it cannot be understood, so the field can show an error
 * instead of silently writing a wrong number.
 */
export function parseHours(input: string): number | null {
  const raw = input.trim().toLowerCase();
  if (!raw) return null;

  // 1:30
  const colon = raw.match(/^(\d+):([0-5]?\d)$/);
  if (colon) return Number(colon[1]) + Number(colon[2]) / 60;

  // 90m
  const mins = raw.match(/^(\d+(?:\.\d+)?)\s*m(?:in(?:utes?)?)?$/);
  if (mins) return Number(mins[1]) / 60;

  // 1h30 / 1h 30m / 1h
  const hm = raw.match(/^(\d+(?:\.\d+)?)\s*h(?:ours?|rs?)?\s*(\d+)?\s*m?(?:in)?s?$/);
  if (hm) return Number(hm[1]) + (hm[2] ? Number(hm[2]) / 60 : 0);

  // plain decimal
  const dec = Number(raw);
  if (!Number.isNaN(dec) && dec >= 0) return dec;

  return null;
}

/** Seconds -> H:MM:SS, for the running timer. */
export function elapsed(seconds: number) {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

const dayFmt = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric'
});
const longFmt = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric'
});

/**
 * Dates on the wire are plain `YYYY-MM-DD` day strings with no timezone.
 * Parsing them with `new Date()` shifts them by the local UTC offset, which is
 * the source of the off-by-one-day bugs in v1. Always go through here.
 */
export function parseDay(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

export function toDayString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function today(): string {
  return toDayString(new Date());
}

export function formatDay(day: string | null | undefined) {
  if (!day) return '—';
  return dayFmt.format(parseDay(day));
}

export function formatDate(day: string | null | undefined) {
  if (!day) return '—';
  return longFmt.format(parseDay(day));
}

export function formatRelativeDay(day: string) {
  const target = parseDay(day);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const diff = Math.round((target.getTime() - now.getTime()) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === -1) return 'Yesterday';
  if (diff === 1) return 'Tomorrow';
  return formatDay(day);
}

export function addDays(day: string, delta: number) {
  const d = parseDay(day);
  d.setDate(d.getDate() + delta);
  return toDayString(d);
}

export function startOfWeek(day: string) {
  const d = parseDay(day);
  d.setDate(d.getDate() - d.getDay());
  return toDayString(d);
}

export function initials(name: string | null | undefined) {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
}
